import { useState } from "react";
import { Link } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MfgItemPicker } from "@/components/mfg/MfgItemPicker";
import { mfgCatalogApi, mfgCatalogKeys, useMfgItemChildren, type MfgItemDetail, type MfgItemHierarchyEdge, type MfgItemRow } from "@/lib/mfg-catalog";
import { ApiError } from "@/lib/api";

// Backend serializes the cycle-guard error code into ProblemDetails.title (see
// MfgCatalogEndpoints.Guard → Results.Problem(title: ex.Code, …)); apiFetch stores
// the parsed body on ApiError.body. So we match ApiError.body.title against these codes.
const CYCLE_CODES: Record<string, string> = {
  MFG_HIERARCHY_CYCLE: "Can't add — this would create a loop in the tree.",
  MFG_HIERARCHY_SELF_REFERENCE: "An item can't be its own child.",
  MFG_HIERARCHY_DUPLICATE: "That item is already a child here.",
};

const DRILLABLE = new Set(["Assembly", "Variant", "Alternative"]);

export function ChildrenSection({ item }: { item: MfgItemDetail }) {
  const qc = useQueryClient();
  const { data: children } = useMfgItemChildren(item.mfgItemId);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerError, setPickerError] = useState<string | null>(null);
  const invalidate = () => { void qc.invalidateQueries({ queryKey: mfgCatalogKeys.children(item.mfgItemId) }); void qc.invalidateQueries({ queryKey: mfgCatalogKeys.detail(item.mfgItemId) }); };

  const add = useMutation({
    mutationFn: (child: MfgItemRow) => mfgCatalogApi.addChild(item.mfgItemId, { childItemId: child.mfgItemId, quantity: 1, sortOrder: (children?.length ?? 0) + 1, note: null }),
    onSuccess: () => { invalidate(); setPickerOpen(false); setPickerError(null); toast.success("Child added"); },
    onError: (e) => {
      // Surface the cycle-guard 409 inline on the picker rather than losing context.
      const code = e instanceof ApiError && e.body && typeof e.body === "object" && "title" in e.body ? String((e.body as { title: string }).title) : "";
      setPickerError(CYCLE_CODES[code] ?? `Add failed: ${(e).message}`);
    },
  });
  const patchEdge = useMutation({
    mutationFn: (v: { edge: MfgItemHierarchyEdge; quantity: number; sortOrder: number }) => mfgCatalogApi.updateEdge(v.edge.mfgItemHierarchyId, { quantity: v.quantity, sortOrder: v.sortOrder, note: v.edge.note }, v.edge.rowVersionBase64),
    onSuccess: invalidate,
    onError: (e) => toast.error(`Save failed: ${(e).message}`),
  });
  const del = useMutation({
    mutationFn: (edge: MfgItemHierarchyEdge) => mfgCatalogApi.removeEdge(edge.mfgItemHierarchyId, edge.rowVersionBase64),
    onSuccess: () => { invalidate(); toast.success("Removed"); },
    onError: (e) => toast.error(`Remove failed: ${(e).message}`),
  });

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-muted-foreground">Children</h3>
        <Button size="sm" onClick={() => { setPickerError(null); setPickerOpen(true); }}>+ Add child</Button>
      </div>
      <table className="w-full text-sm">
        <thead className="text-muted-foreground"><tr><th className="text-left">Child</th><th className="text-left">Type</th><th className="text-left">Qty</th><th className="text-left">Sort</th><th></th></tr></thead>
        <tbody>
          {(children ?? []).map((e) => (
            <tr key={e.mfgItemHierarchyId} className="border-t">
              <td>{DRILLABLE.has(e.childItemTypeCode ?? "") ? <Link className="text-primary underline" to={`/admin/mfg-catalog/${e.childItemId}`}>{e.childName}</Link> : e.childName}</td>
              <td>{e.childItemTypeCode}</td>
              <td><Input type="number" defaultValue={e.quantity} className="w-20" onBlur={(ev) => { const q = Number(ev.target.value); if (q !== e.quantity) patchEdge.mutate({ edge: e, quantity: q, sortOrder: e.sortOrder }); }} /></td>
              <td><Input type="number" defaultValue={e.sortOrder} className="w-16" onBlur={(ev) => { const s = Number(ev.target.value); if (s !== e.sortOrder) patchEdge.mutate({ edge: e, quantity: e.quantity, sortOrder: s }); }} /></td>
              <td className="text-right"><Button variant="ghost" size="sm" onClick={() => del.mutate(e)}>Remove</Button></td>
            </tr>
          ))}
          {(children ?? []).length === 0 && <tr><td colSpan={5} className="py-2 text-muted-foreground">No children yet.</td></tr>}
        </tbody>
      </table>

      <Dialog open={pickerOpen} onOpenChange={(o) => { setPickerOpen(o); if (!o) setPickerError(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add a child item</DialogTitle></DialogHeader>
          {pickerError && <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{pickerError}</p>}
          <MfgItemPicker excludeId={item.mfgItemId} onPick={(c) => add.mutate(c)} />
        </DialogContent>
      </Dialog>
    </section>
  );
}
